import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../axiosInstance';
import { LanguageContext } from '../LanguageContext';
import { classifySigningRequests } from '../utils/myDocuments';

const copy = {
  en: {
    title: 'My documents',
    caption: 'Secure documents',
    intro: 'Forms, agreements, and signed copies sent to you by NWAC will appear here.',
    backDashboard: 'Back to dashboard',
    loading: 'Loading documents...',
    loadError: 'We could not load your documents. Please try again.',
    none: 'You do not have any documents yet.',
    noActionRequired: 'There are no documents waiting for your signature right now.',
    summary: {
      actionRequired: 'Action required',
      completed: 'Signed',
      history: 'Other'
    },
    sections: {
      actionRequired: 'Ready for your review',
      completed: 'Signed documents',
      history: 'Cancelled or expired'
    },
    tableHeaders: {
      name: 'Name',
      status: 'Status',
      type: 'Type',
      updated: 'Updated',
      action: 'Action'
    },
    view: 'Open',
    download: 'Download copy',
    downloading: 'Preparing copy…',
    downloadPending: 'Copy not available yet',
    status: {
      pending: 'Pending',
      viewed: 'Viewed',
      signed: 'Signed',
      cancelled: 'Cancelled',
      expired: 'Expired'
    },
    type: {
      'consent-no-prefill': 'Form (No prefill)',
      'consent-cm-prefill': 'Form (CM prefill)',
      'main-intake': 'Main Intake'
    }
  },
  fr: {
    title: 'Mes documents',
    caption: 'Documents sécurisés',
    intro: 'Les formulaires, ententes et copies signées envoyés par l’AFAC apparaîtront ici.',
    backDashboard: 'Retour au tableau de bord',
    loading: 'Chargement des documents…',
    loadError: 'Impossible de charger vos documents. Veuillez réessayer.',
    none: 'Vous n’avez encore aucun document.',
    noActionRequired: 'Aucun document ne nécessite votre signature pour le moment.',
    summary: {
      actionRequired: 'À traiter',
      completed: 'Signés',
      history: 'Autres'
    },
    sections: {
      actionRequired: 'Prêts pour votre révision',
      completed: 'Documents signés',
      history: 'Annulés ou expirés'
    },
    tableHeaders: {
      name: 'Nom',
      status: 'Statut',
      type: 'Type',
      updated: 'Mis à jour',
      action: 'Action'
    },
    view: 'Ouvrir',
    download: 'Télécharger la copie',
    downloading: 'Préparation de la copie…',
    downloadPending: 'Copie non disponible pour le moment',
    status: {
      pending: 'En attente',
      viewed: 'Vu',
      signed: 'Signé',
      cancelled: 'Annulé',
      expired: 'Expiré'
    },
    type: {
      'consent-no-prefill': 'Formulaire (sans préremplissage)',
      'consent-cm-prefill': 'Formulaire (prérempli par le gestionnaire)',
      'main-intake': 'Dossier principal'
    }
  }
};

const statusLabel = (status, t) => t.status[String(status || '').toLowerCase()] || status || '-';
const typeLabel = (type, t) => t.type[type] || type || '-';
const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const renderDocumentTable = ({ items, t, actionRenderer, showType = true }) => (
  <table className="govuk-table">
    <thead className="govuk-table__head">
      <tr className="govuk-table__row">
        <th className="govuk-table__header">{t.tableHeaders.name}</th>
        {showType ? <th className="govuk-table__header">{t.tableHeaders.type}</th> : null}
        <th className="govuk-table__header">{t.tableHeaders.status}</th>
        <th className="govuk-table__header">{t.tableHeaders.updated}</th>
        <th className="govuk-table__header">{t.tableHeaders.action}</th>
      </tr>
    </thead>
    <tbody className="govuk-table__body">
      {items.map(item => (
        <tr key={item.id} className="govuk-table__row">
          <td className="govuk-table__cell">{item.workflow_name || `Form ${item.id}`}</td>
          {showType ? <td className="govuk-table__cell">{typeLabel(item.workflow_type, t)}</td> : null}
          <td className="govuk-table__cell">{statusLabel(item.status, t)}</td>
          <td className="govuk-table__cell">{formatDateTime(item.updated_at || item.created_at)}</td>
          <td className="govuk-table__cell">{actionRenderer(item)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Documents = () => {
  const { language } = useContext(LanguageContext);
  const t = useMemo(() => copy[language] || copy.en, [language]);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/api/signing-requests');
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load signing requests', err);
        if (!cancelled) setError(t.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [language, t.loadError]);

  const visibleItems = useMemo(
    () => classifySigningRequests(items),
    [items]
  );

  const downloadSignedCopy = async (item) => {
    if (!item?.id || downloadingId) return;
    setDownloadingId(item.id);
    setDownloadError('');
    try {
      const { data } = await api.get(`/api/signing-requests/${item.id}/download`);
      if (!data?.download_url) throw new Error('signed_copy_unavailable');
      window.open(data.download_url, '_blank', 'noopener,noreferrer');
    } catch (_) {
      setDownloadError(t.downloadPending);
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (!loading && !error && visibleItems.totalCount === 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [error, loading, navigate, visibleItems.totalCount]);

  return (
    <div className="govuk-width-container govuk-!-margin-top-3">
      <main className="govuk-main-wrapper" role="main">
        <p className="govuk-body govuk-!-margin-bottom-4">
          <Link className="govuk-link" to="/dashboard">{t.backDashboard}</Link>
        </p>
        <section className="app-documents-hero">
          <div className="app-documents-hero__content">
            <p className="govuk-caption-m govuk-!-margin-bottom-2">{t.caption}</p>
            <h1 className="govuk-heading-l govuk-!-margin-bottom-3">{t.title}</h1>
            <p className="govuk-body govuk-!-margin-bottom-0">{t.intro}</p>
          </div>
          <div className="app-documents-summary-grid" aria-label={t.title}>
            <div className="app-documents-summary-card">
              <span className="app-documents-summary-card__label">{t.summary.actionRequired}</span>
              <strong className="app-documents-summary-card__value">{visibleItems.activeCount}</strong>
            </div>
            <div className="app-documents-summary-card">
              <span className="app-documents-summary-card__label">{t.summary.completed}</span>
              <strong className="app-documents-summary-card__value">{visibleItems.completedCount}</strong>
            </div>
            <div className="app-documents-summary-card">
              <span className="app-documents-summary-card__label">{t.summary.history}</span>
              <strong className="app-documents-summary-card__value">{visibleItems.archivedCount}</strong>
            </div>
          </div>
        </section>
        {loading && <p className="govuk-body">{t.loading}</p>}
        {error && (
          <div className="govuk-error-summary" role="alert" aria-live="assertive">
            <h2 className="govuk-error-summary__title">{error}</h2>
          </div>
        )}
        {downloadError && <p className="govuk-error-message" role="alert">{downloadError}</p>}
        {!loading && !error && visibleItems.totalCount > 0 && visibleItems.activeCount === 0 && (
          <div className="app-surface-panel govuk-!-margin-bottom-5">
            <p className="govuk-body govuk-!-margin-bottom-0">{t.noActionRequired}</p>
          </div>
        )}
        {!loading && !error && visibleItems.activeCount > 0 && (
          <section className="app-surface-panel govuk-!-margin-bottom-5">
            <h2 className="govuk-heading-m">{t.sections.actionRequired}</h2>
            {renderDocumentTable({
              items: visibleItems.activeItems,
              t,
              showType: false,
              actionRenderer: (item) => (
                <Link className="govuk-link" to={`/documents/${item.id}`}>{t.view}</Link>
              ),
            })}
          </section>
        )}
        {!loading && !error && visibleItems.completedCount > 0 && (
          <section className="app-surface-panel govuk-!-margin-bottom-5">
            <h2 className="govuk-heading-m">{t.sections.completed}</h2>
            {renderDocumentTable({
              items: visibleItems.completedItems,
              t,
              actionRenderer: (item) => (
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                  disabled={downloadingId !== null}
                  onClick={() => downloadSignedCopy(item)}
                >
                  {downloadingId === item.id ? t.downloading : t.download}
                </button>
              ),
            })}
          </section>
        )}
        {!loading && !error && visibleItems.archivedCount > 0 && (
          <section className="app-surface-panel">
            <h2 className="govuk-heading-m">{t.sections.history}</h2>
            {renderDocumentTable({
              items: visibleItems.archivedItems,
              t,
              actionRenderer: () => <span className="govuk-hint">-</span>,
            })}
          </section>
        )}
      </main>
    </div>
  );
};

export default Documents;
